import { fileTypeFromBuffer } from 'file-type';

export const SECURITY_CONFIG_UPLOAD = {
    allowedTypes: {
        'application/pdf': ['.pdf'],
        'application/msword': ['.doc'],
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
        'application/vnd.ms-excel': ['.xls'],
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
        'application/vnd.ms-excel.sheet.macroEnabled.12': ['.xlsm'],
        'application/vnd.ms-excel.sheet.macroenabled.12': ['.xlsm'],
        'text/csv': ['.csv'],
        'text/plain': ['.txt'],
        'image/jpeg': ['.jpg', '.jpeg'],
        'image/jpg': ['.jpg', '.jpeg'],
        'image/png': ['.png'],
        'application/zip': ['.zip'],
        'application/x-zip-compressed': ['.zip'],
    },
    maxFileSize: 100 * 1024 * 1024, // 100MB
    maxFiles: 50,
    nameMaxLength: 255,
}

// Liste de toutes les extensions autorisées
const getAllowedExtensions = (): string[] => {
    const extensions = new Set<string>();
    Object.values(SECURITY_CONFIG_UPLOAD.allowedTypes).forEach(exts => {
        exts.forEach(ext => extensions.add(ext.toLowerCase()));
    });
    return Array.from(extensions);
}

// Vérifie si un fichier est autorisé en se basant sur le type MIME et l'extension
export const isFileAllowed = (file: File): boolean => {
    // Vérifie d'abord par type MIME
    if (SECURITY_CONFIG_UPLOAD.allowedTypes[file.type as keyof typeof SECURITY_CONFIG_UPLOAD.allowedTypes]) {
        return true;
    }
    
    // Si le type MIME n'est pas reconnu, vérifie par extension
    const fileName = file.name.toLowerCase();
    const allowedExtensions = getAllowedExtensions();
    
    return allowedExtensions.some(ext => fileName.endsWith(ext));
}

// Récupère le type MIME correct basé sur l'extension du fichier (pour les formats Office basés sur ZIP)
const getMimeTypeFromExtension = (fileName: string): string | null => {
    const lowerFileName = fileName.toLowerCase();
    
    // Pour les fichiers Office basés sur ZIP, on doit se fier à l'extension
    if (lowerFileName.endsWith('.xlsx')) {
        return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
    }
    if (lowerFileName.endsWith('.xlsm')) {
        return 'application/vnd.ms-excel.sheet.macroEnabled.12';
    }
    if (lowerFileName.endsWith('.docx')) {
        return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
    }
    
    // Pour les autres types, chercher dans la config
    for (const [mimeType, extensions] of Object.entries(SECURITY_CONFIG_UPLOAD.allowedTypes)) {
        for (const ext of extensions) {
            if (lowerFileName.endsWith(ext)) {
                return mimeType;
            }
        }
    }
    
    return null;
}

// Détecte le vrai type MIME du fichier et le corrige si nécessaire
export const detectAndFixFileMimeType = async (file: File): Promise<File> => {
    try {
        // Lire les premiers octets du fichier pour détecter son type réel
        const buffer = await file.arrayBuffer();
        const uint8Array = new Uint8Array(buffer);
        
        // Détecter le type réel via les magic bytes
        const detectedType = await fileTypeFromBuffer(uint8Array);
        
        // Déterminer le type MIME correct à utiliser
        let correctMimeType: string | null = null;
        
        // Si file-type détecte le type et qu'il est dans notre liste autorisée, l'utiliser
        if (detectedType && SECURITY_CONFIG_UPLOAD.allowedTypes[detectedType.mime as keyof typeof SECURITY_CONFIG_UPLOAD.allowedTypes]) {
            correctMimeType = detectedType.mime;
        }
        // Sinon, pour les formats Office (ZIP-based), se baser sur l'extension
        else if (detectedType?.mime === 'application/zip' || !detectedType) {
            correctMimeType = getMimeTypeFromExtension(file.name);
        }
        // Sinon, utiliser le type détecté par file-type
        else if (detectedType) {
            correctMimeType = detectedType.mime;
        }
        
        // Si on a trouvé un type MIME correct et qu'il diffère de celui du fichier
        if (correctMimeType && correctMimeType !== file.type) {
            return new File([file], file.name, { 
                type: correctMimeType,
                lastModified: file.lastModified 
            });
        }
        
        // Sinon, retourner le fichier tel quel
        return file;
        
    } catch (error) {
        // En cas d'erreur, essayer au moins de corriger par extension
        const mimeFromExtension = getMimeTypeFromExtension(file.name);
        if (mimeFromExtension && mimeFromExtension !== file.type) {
            return new File([file], file.name, { 
                type: mimeFromExtension,
                lastModified: file.lastModified 
            });
        }
        return file;
    }
}